import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";

import {
  getMembersList,
  getTotalNumberOfMembers,
  getOccupiedSeatCount,
  getMembersLoading,
  getPurchasedSeatCount,
  getPaginatedTotalMembers,
  getPaginatedOccupiedSeats,
  getPaginatedPurchasedSeats,
} from "selectors/MembersSelectors";
import {
  clearMemberSelection,
  fetchMembers,
  inviteMembers,
  setSeatAssignLoading,
  setSeatUnassignLoading,
  updateMembers,
} from "actions/MembersActions";
import {
  MemberQueryParams,
  MemberRoleFilter,
  MemberSortField,
  MemberSortOrder,
  UpdateMember,
} from "types/MembersTypes";

export const useMembers = () => {
  const dispatch = useDispatch();
  const memberList = useSelector(getMembersList);
  const totalNumberOfMembers = useSelector(getTotalNumberOfMembers) || 0;
  const occupiedSeatCount = useSelector(getOccupiedSeatCount) || 0;
  const membersLoading = useSelector(getMembersLoading);
  const purchasedSeatCount = useSelector(getPurchasedSeatCount);
  const paginatedTotalMembers = useSelector(getPaginatedTotalMembers) || 0;
  const paginatedOccupiedSeats = useSelector(getPaginatedOccupiedSeats) || 0;
  const paginatedPurchasedSeats = useSelector(getPaginatedPurchasedSeats) || 0;

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortBy, setSortBy] = useState<MemberSortField | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<MemberSortOrder>("ASC");
  const [roleFilter, setRoleFilter] = useState<MemberRoleFilter | undefined>(
    undefined,
  );
  const [seatAssignedFilter, setSeatAssignedFilter] = useState<
    boolean | undefined
  >(undefined);

  const observableItemRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<number>(0);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Current search + filter + sort context, threaded into every fetch
  const queryParams: MemberQueryParams = useMemo(
    () => ({
      email: searchTerm,
      role: roleFilter,
      seatAssigned: seatAssignedFilter,
      sortBy,
      sortOrder: sortBy ? sortOrder : undefined,
    }),
    [searchTerm, roleFilter, seatAssignedFilter, sortBy, sortOrder],
  );

  // debounce the fetchMembers action
  const debouncedFetchMembers = useMemo(
    () =>
      debounce((params: MemberQueryParams) => {
        dispatch(fetchMembers(params));
      }, 500),
    [dispatch],
  );

  const prevSearchTermRef = useRef<string>(searchTerm);

  // Refetch from page 0 when search, filters or sort change. Search input is
  // debounced (user is typing); filter/sort changes fetch immediately, and so
  // does the initial mount (prevSearchTermRef starts equal to searchTerm).
  // Filter/sort changes also clear row selection here; search changes clear it
  // in MembersList's own effect, which watches searchTerm and list resets.
  useEffect(() => {
    pageRef.current = 0;

    const searchChanged = prevSearchTermRef.current !== searchTerm;
    prevSearchTermRef.current = searchTerm;

    if (searchChanged) {
      debouncedFetchMembers(queryParams);
    } else {
      debouncedFetchMembers.cancel();
      // A sort/filter change can reorder or exclude selected rows
      dispatch(clearMemberSelection());
      dispatch(fetchMembers(queryParams));
    }

    return () => {
      debouncedFetchMembers.cancel();
    };
  }, [searchTerm, queryParams, debouncedFetchMembers, dispatch]);

  // observer for infinite scroll
  useEffect(() => {
    const element = observableItemRef.current;

    if (
      !element ||
      memberList.length === 0 ||
      paginatedTotalMembers <= memberList.length ||
      membersLoading
    )
      return;

    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        const newPage = pageRef.current + 1;
        pageRef.current = newPage;
        dispatch(fetchMembers({ page: newPage, ...queryParams }));
      }
    });

    observerRef.current.observe(element);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    dispatch,
    memberList.length,
    membersLoading,
    queryParams,
    paginatedTotalMembers,
  ]);

  // Cycle a column through ascending -> descending -> unsorted
  const toggleSort = useCallback(
    (field: MemberSortField) => {
      if (sortBy !== field) {
        setSortBy(field);
        setSortOrder("ASC");
      } else if (sortOrder === "ASC") {
        setSortOrder("DESC");
      } else {
        setSortBy(undefined);
        setSortOrder("ASC");
      }
    },
    [sortBy, sortOrder],
  );

  // Check if all seats are utilized
  const isSeatsFullyUtilized = useMemo(
    () => occupiedSeatCount === purchasedSeatCount,
    [occupiedSeatCount, purchasedSeatCount],
  );

  // show infinte scroll loading when there are more members to fetch
  const showInfiniteScrollLoading = useMemo(
    () =>
      memberList.length > 0 &&
      paginatedTotalMembers > memberList.length &&
      !membersLoading &&
      paginatedTotalMembers > 15,
    [memberList.length, paginatedTotalMembers, membersLoading],
  );

  // Assign seats to members
  const assignSeatsToMembers = useCallback(
    (membersToAssign: UpdateMember[]) => {
      if (isSeatsFullyUtilized) {
        return;
      }

      pageRef.current = 0;
      dispatch(setSeatAssignLoading(true));
      dispatch(
        updateMembers({
          members: membersToAssign,
          page: pageRef.current,
          ...queryParams,
        }),
      );
    },
    [dispatch, isSeatsFullyUtilized, queryParams],
  );

  // Unassign seats to members
  const unassignSeatsToMembers = useCallback(
    (membersToUnassign: UpdateMember[]) => {
      pageRef.current = 0;
      dispatch(setSeatUnassignLoading(true));
      dispatch(
        updateMembers({
          members: membersToUnassign,
          page: pageRef.current,
          ...queryParams,
        }),
      );
    },
    [dispatch, queryParams],
  );

  // Invite members
  const onInviteMembers = useCallback(
    (emails: string[]) => {
      dispatch(
        inviteMembers({
          emails,
          page: pageRef.current,
          ...queryParams,
        }),
      );
    },
    [dispatch, queryParams],
  );

  return {
    memberList,
    membersLoading,
    occupiedSeatCount,
    purchasedSeatCount,
    searchTerm,
    setSearchTerm,
    sortBy,
    sortOrder,
    toggleSort,
    roleFilter,
    setRoleFilter,
    seatAssignedFilter,
    setSeatAssignedFilter,
    queryParams,
    totalNumberOfMembers,
    observableItemRef,
    isSeatsFullyUtilized,
    assignSeatsToMembers,
    unassignSeatsToMembers,
    onInviteMembers,
    showInfiniteScrollLoading,
    paginatedTotalMembers,
    paginatedOccupiedSeats,
    paginatedPurchasedSeats,
  };
};
